import { API_BASE, authHeaders } from './apiAuth'

/**
 * A project's own images: what was dropped on a board, and what a skill made.
 *
 * A separate store from the library, on purpose. The library is company
 * photography used as evidence in competitive work; these are working
 * material. Keeping them apart means the library is the library because of
 * where a thing is, not because of a flag that every query has to remember.
 */

export interface ProjectFile {
  id: string
  path: string
  mime_type: string
  width: number | null
  height: number | null
  origin: 'upload' | 'generated' | 'from-library' | 'from-miro' | 'from-amazon' | 'from-web'
  made_by: Record<string, unknown>
  title: string
  created_at: string
  /** Signed, and good for two hours. Never stored anywhere. */
  url: string
  /**
   * A smaller copy to draw, for a large picture, when asked for with
   * `thumbs`. Absent for a small one: it is its own thumbnail.
   */
  thumb?: string
  /**
   * A large picture with no drawing copy yet: this browser makes one
   * (makeMissingCopies) and the `thumb` link serves it from then on.
   */
  needsCopy?: boolean
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (typeof body.error === 'string' && body.error) return body.error
  } catch {
    /* fall through to the status */
  }
  return `Something went wrong (HTTP ${response.status})`
}

export async function listProjectFiles(
  projectId: string,
  /**
   * `copies: false` -- only to look at (a picker): the server does not check
   * each picture for a missing drawing copy, which costs one look in R2 per
   * file and made a long list slow to open (2026-10-09).
   */
  options: { thumbs?: boolean; copies?: boolean } = {},
): Promise<ProjectFile[]> {
  const response = await fetch(
    `${API_BASE}/api/project-files?project=${encodeURIComponent(projectId)}${options.thumbs ? '&thumbs=1' : ''}${
      options.copies === false ? '&copies=0' : ''
    }`,
    { headers: { Accept: 'application/json', ...(await authHeaders()) } },
  )
  if (!response.ok) throw new Error(await reason(response))
  const rows = (await response.json()) as ProjectFile[]
  if (options.thumbs) void makeMissingCopies(projectId, rows)
  return rows
}

/* -------------------------------------------------------------------------
   Drawing copies, made here rather than by Supabase (2026-10-01)
   ------------------------------------------------------------------------- */

/**
 * A large picture is drawn on a board from a copy at most this many pixels
 * on its longer side -- the full-size file is laid over it only when the
 * board is zoomed in past it (canvas/nodes.tsx).
 *
 * These copies used to be made by Supabase's image transformation, which is
 * counted per picture per month and ran over the plan on the first of the
 * month. So the browser makes them: once, when a picture is uploaded or first
 * drawn, and hands them to the Worker, which keeps them in R2 for good.
 */
const COPY_EDGE = 1024
/** Copies being made, or already asked for, in this tab: each one once. */
const copying = new Set<string>()

/** A copy of this picture at drawing size: PNG if it is see-through, JPEG if not. */
export async function drawingCopyOf(picture: Blob): Promise<Blob | null> {
  const bitmap = await createImageBitmap(picture)
  try {
    const scale = Math.min(1, COPY_EDGE / Math.max(bitmap.width, bitmap.height, 1))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return null
    context.imageSmoothingQuality = 'high'
    context.drawImage(bitmap, 0, 0, width, height)
    let transparent = false
    const pixels = context.getImageData(0, 0, width, height).data
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 250) {
        transparent = true
        break
      }
    }
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, transparent ? 'image/png' : 'image/jpeg', transparent ? undefined : 0.88),
    )
  } finally {
    bitmap.close()
  }
}

/** Hand a drawing copy to the Worker. Quiet: a copy that did not go is made next time. */
async function sendDrawingCopy(projectId: string, fileId: string, copy: Blob): Promise<void> {
  const response = await fetch(
    `${API_BASE}/api/project-files/draw?project=${encodeURIComponent(projectId)}&file=${encodeURIComponent(fileId)}`,
    { method: 'POST', headers: { 'Content-Type': copy.type, ...(await authHeaders()) }, body: copy },
  )
  if (!response.ok) throw new Error(await reason(response))
}

/** Make and send the copy of one picture from bytes already in hand. */
async function copyFrom(projectId: string, fileId: string, picture: Blob): Promise<void> {
  const copy = await drawingCopyOf(picture)
  if (copy) await sendDrawingCopy(projectId, fileId, copy)
}

/**
 * Every large picture on this board that has no drawing copy yet: fetched
 * once (its link serves the original until there is a copy), shrunk here,
 * sent back. Two at a time, in the background; nothing waits for it.
 */
async function makeMissingCopies(projectId: string, rows: ProjectFile[]): Promise<void> {
  const todo = rows.filter((row) => row.needsCopy && row.thumb && !copying.has(row.id))
  for (const row of todo) copying.add(row.id)
  let next = 0
  const lane = async () => {
    while (next < todo.length) {
      const row = todo[next]
      next += 1
      try {
        const response = await fetch(row.thumb as string)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        await copyFrom(projectId, row.id, await response.blob())
      } catch (error) {
        copying.delete(row.id)
        console.warn('[WonderWorks] could not make a drawing copy', row.id, error)
      }
    }
  }
  await Promise.all([lane(), lane()])
}

/** Formats Miro places as they are. Anything else is turned into one of these. */
const BOARD_SAFE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif'])

/**
 * The picture as PNG or JPEG, if it is in a format Miro will not place.
 *
 * Every picture in a project can end up on a Miro board, and the server
 * places them there by handing Miro their address -- it cannot change a
 * picture's format on the way. So a WebP, AVIF, SVG or anything else is
 * redrawn here, in the browser, before it is stored (2026-09-30). PNG when it
 * uses transparency, which a cut-out pack shot does; JPEG otherwise, which is
 * what the platform stores photographs as. Same size in pixels.
 */
export async function boardSafeFile(file: File): Promise<File> {
  if (BOARD_SAFE_TYPES.has(file.type) || !file.type.startsWith('image/')) return file
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = url
    await image.decode()
    const width = image.naturalWidth || 1024
    const height = image.naturalHeight || 1024
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(image, 0, 0, width, height)

    let transparent = false
    const pixels = context.getImageData(0, 0, width, height).data
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 250) {
        transparent = true
        break
      }
    }
    const type = transparent ? 'image/png' : 'image/jpeg'
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, transparent ? undefined : 0.92),
    )
    if (!blob) return file
    const stem = file.name.replace(/\.[a-z0-9]+$/i, '') || 'picture'
    return new File([blob], `${stem}.${transparent ? 'png' : 'jpg'}`, { type })
  } catch (error) {
    // Left as it is; the server will say so if it cannot be placed on a board.
    console.warn('could not turn the picture into PNG or JPEG', file.name, error)
    return file
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Put one image into a project.
 *
 * The bytes go up as the body rather than in a form: a Worker reading a
 * multipart body has to parse it, and there is only ever one file here.
 * Anything Miro would not place goes up as PNG or JPEG (boardSafeFile).
 */
export async function uploadProjectFile(
  projectId: string,
  original: File,
  size?: { width: number; height: number },
  /** Put here as a reference: listed in the generate picker's History. */
  options: { reference?: boolean } = {},
): Promise<ProjectFile> {
  const file = await boardSafeFile(original)
  const params = new URLSearchParams({ project: projectId, name: file.name })
  if (options.reference) params.set('for', 'reference')
  if (size) {
    params.set('w', String(size.width))
    params.set('h', String(size.height))
  }

  const response = await fetch(`${API_BASE}/api/project-files?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type, ...(await authHeaders()) },
    body: file,
  })
  if (!response.ok) throw new Error(await reason(response))
  const stored = (await response.json()) as ProjectFile
  // A large picture's drawing copy is made now, from the bytes in hand, so the
  // board never has to fetch the original to make one (see makeMissingCopies).
  const big =
    file.type !== 'image/gif' &&
    file.type !== 'image/svg+xml' &&
    (file.size > 1.5 * 1024 * 1024 || !size || Math.max(size.width, size.height) > 2048)
  if (big && !copying.has(stored.id)) {
    copying.add(stored.id)
    void copyFrom(projectId, stored.id, file).catch((error: unknown) => {
      copying.delete(stored.id)
      console.warn('[WonderWorks] could not make a drawing copy', stored.id, error)
    })
  }
  return stored
}

/**
 * Copy pictures into a project, so a board there can show them.
 *
 * A board reads only its own project's files — which is what stops one
 * person's file id resolving on another's board, and what makes a shared board
 * show the same thing to everybody. So a picture that came from somewhere else
 * has to arrive properly rather than be pointed at.
 *
 * Answers with a map of the id given to the id in this project. A picture
 * already there keeps the id it already has.
 */
export async function copyProjectFiles(
  projectId: string,
  fileIds: string[],
): Promise<Record<string, string>> {
  if (fileIds.length === 0) return {}
  const response = await fetch(
    `${API_BASE}/api/project-files/copy?project=${encodeURIComponent(projectId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ files: fileIds }),
    },
  )
  if (!response.ok) throw new Error(await reason(response))
  return (await response.json()) as Record<string, string>
}

/**
 * Library pictures copied into a project ("Copy to project").
 *
 * A copy, not a reference: see `copyFromLibrary` in the Worker. Answers asset
 * id -> the new file's id in this project, for the ones that worked; one
 * missing from the answer could not be copied. Forty at a time at most.
 */
export async function copyLibraryToProject(
  projectId: string,
  assetIds: string[],
): Promise<Record<string, string>> {
  if (assetIds.length === 0) return {}
  const response = await fetch(
    `${API_BASE}/api/project-files/copy?project=${encodeURIComponent(projectId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ library: assetIds }),
    },
  )
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Record<string, string>
  const made: Record<string, string> = {}
  for (const [key, id] of Object.entries(body)) {
    if (key.startsWith('library:')) made[key.slice('library:'.length)] = id
  }
  return made
}

/**
 * Take pictures off the generate picker's History, without deleting them: runs
 * that used them still show them (2026-10-15).
 */
export async function unlistReferences(projectId: string, fileIds: string[]): Promise<number> {
  if (fileIds.length === 0) return 0
  const response = await fetch(`${API_BASE}/api/project-files?project=${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ unlist: fileIds }),
  })
  if (!response.ok) throw new Error(await reason(response))
  return Number(((await response.json()) as { unlisted?: number }).unlisted) || 0
}

/**
 * Take a picture out of a project for good.
 *
 * Both the file and its bytes. Anything that referred to it — a run that used
 * it as a reference — will no longer be able to show it, which is what
 * deleting a picture means.
 */
export async function deleteProjectFile(projectId: string, fileId: string): Promise<void> {
  const params = new URLSearchParams({ project: projectId, file: fileId })
  const response = await fetch(`${API_BASE}/api/project-files?${params.toString()}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  })
  if (!response.ok) throw new Error(await reason(response))
}

/** How big the picture actually is, so it lands on the board at its own shape. */
export function measure(file: File): Promise<{ width: number; height: number } | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
      URL.revokeObjectURL(url)
    }
    // Not an error worth stopping for: the file still uploads, it just lands
    // as a square until it is resized.
    image.onerror = () => {
      resolve(undefined)
      URL.revokeObjectURL(url)
    }
    image.src = url
  })
}
